import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, TextField, IconButton, ToggleButton, ToggleButtonGroup, Checkbox, Menu, MenuItem } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import TableChartIcon from '@mui/icons-material/TableChart';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import { toast } from 'react-toastify';
import { wrapHtmlForPreview } from '../../../Utils/profitAndLoss/profitAndLossPreviewUtils';
import ReportsService from '../../../Services/reports.service';
import {
  parseEditableAmount,
  formatEditableAmount,
  getExportHtmlFromIframe,
  cleanProfitAndLossHtmlForExport,
  setupEditableCells,
  applyValueToSelectedCells,
  clearSelectionInDocument,
  getEditValueForSelection,
  downloadEditableProfitAndLossExcel,
  applyLocalEditsToDocument,
  applyAllLocalEditsToDocument,
  flushPendingInlineEdit,
  syncPartnersEquityWithDocument,
  EDITABLE_SOURCE_LEDGER,
  EDITABLE_SOURCE_PL_REPORT,
} from './editableProfitAndLossUtils';
import { applyPartnersEquitySection } from '../../../Utils/partners/partnersEquityDistribution';
import { EXCEL_COLUMN_MODES } from './editableProfitAndLossExcelExport';
import './EditableProfitAndLossHtmlPreview.css';

const EditableProfitAndLossHtmlPreview = ({
  open = false,
  htmlContent = '',
  onCancel = () => {},
  t = (s) => s,
  title = 'EditableProfitAndLossReport',
  client = null,
  token = '',
  isMonthly = true,
  direction = 'rtl',
  showPercentageColumn = false,
  setShowPercentageColumn = () => {},
  reportData = null,
  showAccountCardsWithZeros = true,
  source = EDITABLE_SOURCE_LEDGER,
  partners = [],
}) => {
  const [htmlUrl, setHtmlUrl] = useState('');
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedCellIds, setSelectedCellIds] = useState(() => new Set());
  const [editValue, setEditValue] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [excelMenuAnchor, setExcelMenuAnchor] = useState(null);
  const [pdfMenuAnchor, setPdfMenuAnchor] = useState(null);
  const [iframeHtmlContent, setIframeHtmlContent] = useState('');
  const [iframeReloadToken, setIframeReloadToken] = useState(0);

  const iframeRef = useRef(null);
  const cleanupEditableRef = useRef(null);
  const multiSelectModeRef = useRef(multiSelectMode);
  const selectedCellIdsRef = useRef(selectedCellIds);
  const editValueRef = useRef(editValue);
  const originalHtmlRef = useRef('');
  const toolbarRef = useRef(null);
  const editInputRef = useRef(null);
  const skipBlurApplyRef = useRef(false);
  const localEditsRef = useRef(new Map());
  const localRenamesRef = useRef(new Map());
  const localDeletedRowsRef = useRef(new Set());
  const localAddedRowsRef = useRef([]);
  const addedRowCounterRef = useRef(0);

  multiSelectModeRef.current = multiSelectMode;
  selectedCellIdsRef.current = selectedCellIds;
  editValueRef.current = editValue;

  const isRtl = direction === 'rtl';
  const showExcelDownload =
    (source === EDITABLE_SOURCE_LEDGER || source === EDITABLE_SOURCE_PL_REPORT) &&
    Boolean(htmlContent);

  useEffect(() => {
    if (!htmlContent || !open) {
      setIframeHtmlContent('');
      return;
    }

    // Amounts are filled from the document total once the iframe loads (see syncPartnersEquityWithDocument).
    const htmlWithPartners = applyPartnersEquitySection(htmlContent, partners, 0);
    // Keep a pristine copy of the latest parent HTML so Reset restores all rows/values.
    originalHtmlRef.current = htmlWithPartners;
    setIframeHtmlContent(htmlWithPartners);
  }, [htmlContent, open, partners]);

  useEffect(() => {
    if (!iframeHtmlContent || !open) {
      setHtmlUrl('');
      return undefined;
    }

    const previewHtml = wrapHtmlForPreview(iframeHtmlContent, {
      includeEditableStyles: true,
      direction,
    });
    const blob = new Blob([previewHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    setHtmlUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [iframeHtmlContent, open, direction, iframeReloadToken]);

  useEffect(() => {
    if (!open) {
      originalHtmlRef.current = '';
      localEditsRef.current.clear();
      localRenamesRef.current.clear();
      localDeletedRowsRef.current.clear();
      localAddedRowsRef.current = [];
      addedRowCounterRef.current = 0;
      setIframeHtmlContent('');
      setIframeReloadToken(0);
      setSelectedCellIds(new Set());
      setEditValue('');
      setMultiSelectMode(false);
      setExcelMenuAnchor(null);
      setPdfMenuAnchor(null);
    }
  }, [open]);

  const handleEditCommitted = useCallback((key, value) => {
    if (value == null) {
      localEditsRef.current.delete(key);
      return;
    }
    localEditsRef.current.set(key, value);
  }, []);

  const handleRenameCommitted = useCallback((rowKey, value) => {
    if (value == null) {
      localRenamesRef.current.delete(rowKey);
      return;
    }
    localRenamesRef.current.set(rowKey, value);
  }, []);

  const handleRowDeleted = useCallback((rowKey) => {
    localDeletedRowsRef.current.add(rowKey);
  }, []);

  const handleRowAdded = useCallback((descriptor) => {
    localAddedRowsRef.current.push(descriptor);
  }, []);

  const handleRowAddedRemoved = useCallback((localRowId) => {
    localAddedRowsRef.current = localAddedRowsRef.current.filter(
      (row) => row.localRowId !== localRowId,
    );
    [...localEditsRef.current.keys()].forEach((key) => {
      if (key.startsWith(`added:${localRowId}`)) {
        localEditsRef.current.delete(key);
      }
    });
    [...localRenamesRef.current.keys()].forEach((key) => {
      if (key === `added:${localRowId}`) {
        localRenamesRef.current.delete(key);
      }
    });
  }, []);

  const getNextAddedRowId = useCallback(() => {
    addedRowCounterRef.current += 1;
    return addedRowCounterRef.current;
  }, []);

  const handleShowPercentageColumnToggle = (checked) => {
    const doc = iframeRef.current?.contentDocument;
    flushPendingInlineEdit(doc, {
      onEditCommitted: handleEditCommitted,
      onRenameCommitted: handleRenameCommitted,
      source,
    });
    setShowPercentageColumn(checked);
    setSelectedCellIds(new Set());
    setEditValue('');
  };

  const syncEditValueFromSelection = useCallback((ids) => {
    const doc = iframeRef.current?.contentDocument;
    const nextValue = getEditValueForSelection(doc, ids);
    setEditValue(nextValue);
    if (ids.size > 0) {
      requestAnimationFrame(() => {
        editInputRef.current?.focus();
        editInputRef.current?.select();
      });
    }
  }, []);

  const handleSelectionChange = useCallback((ids) => {
    setSelectedCellIds(ids);
    syncEditValueFromSelection(ids);
  }, [syncEditValueFromSelection]);

  const handleInvalidAmount = useCallback(() => {
    toast.error(t('InvalidAmount') || 'Invalid amount');
  }, [t]);

  const attachEditableListeners = useCallback(() => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument;
    if (!doc) return;

    if (cleanupEditableRef.current) {
      cleanupEditableRef.current();
      cleanupEditableRef.current = null;
    }

    cleanupEditableRef.current = setupEditableCells(doc, {
      getMultiSelectMode: () => multiSelectModeRef.current,
      getSelectedIds: () => selectedCellIdsRef.current,
      onSelectionChange: handleSelectionChange,
      direction,
      onInvalid: handleInvalidAmount,
      onEditCommitted: handleEditCommitted,
      onRenameCommitted: handleRenameCommitted,
      onRowDeleted: handleRowDeleted,
      onRowAdded: handleRowAdded,
      onRowAddedRemoved: handleRowAddedRemoved,
      getNextAddedRowId,
      deletedRows: localDeletedRowsRef.current,
      addedRows: localAddedRowsRef.current,
      source,
      addRowLabel: t('AddRow'),
    });

    applyAllLocalEditsToDocument(
      doc,
      {
        amountEdits: localEditsRef.current,
        renames: localRenamesRef.current,
      },
      source,
    );
    syncPartnersEquityWithDocument(doc, source);
  }, [
    handleSelectionChange,
    direction,
    handleInvalidAmount,
    handleEditCommitted,
    handleRenameCommitted,
    handleRowDeleted,
    handleRowAdded,
    handleRowAddedRemoved,
    getNextAddedRowId,
    source,
    t,
  ]);

  const handleIframeLoad = () => {
    attachEditableListeners();
  };

  const getCleanedExportHtml = (columnMode = EXCEL_COLUMN_MODES.ALL) => {
    flushPendingInlineEdit(iframeRef.current?.contentDocument, {
      onEditCommitted: handleEditCommitted,
      onRenameCommitted: handleRenameCommitted,
      source,
    });
    const raw = getExportHtmlFromIframe(iframeRef.current, { columnMode });
    return cleanProfitAndLossHtmlForExport(raw);
  };

  const handlePrint = () => {
    flushPendingInlineEdit(iframeRef.current?.contentDocument, {
      onEditCommitted: handleEditCommitted,
      onRenameCommitted: handleRenameCommitted,
      source,
    });

    const raw = getExportHtmlFromIframe(iframeRef.current);
    if (!raw) {
      toast.error(t('NoDataToDisplay') || 'No data to display');
      return;
    }

    const cleanedHtml = cleanProfitAndLossHtmlForExport(raw)
      .replace(/border: 1px solid #ccc;/g, '')
      .replace(/box-shadow: 0px 0px 10px rgba\(0, 0, 0, 0\.1\);/g, '');

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error(t('PrintBlocked') || 'Print blocked by browser');
      return;
    }
    printWindow.document.write(cleanedHtml);
    printWindow.document.close();
    printWindow.print(cleanedHtml);
  };

  const handleDownload = async (columnMode = EXCEL_COLUMN_MODES.ALL) => {
    setPdfMenuAnchor(null);

    const cleanedHtml = getCleanedExportHtml(columnMode);
    if (!cleanedHtml) {
      toast.error(t('NoDataToDisplay') || 'No data to display');
      return;
    }

    const baseName = client?.name
      ? `${client.name}_EditableProfitAndLoss`
      : 'EditableProfitAndLoss';
    const suffix = !isMonthly
      ? 'Annual'
      : columnMode === EXCEL_COLUMN_MODES.TOTALS
        ? 'Totals'
        : 'Monthly';
    const fallbackFileName = `${baseName}_${suffix}.html`;

    setDownloading(true);
    try {
      if (client?.caseId && token) {
        const { blob, fileName } = await ReportsService.convertHtmlToPdf(
          client.caseId,
          cleanedHtml,
          token
        );
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName || `${baseName}_${suffix}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        throw new Error('Missing client or token');
      }
    } catch (err) {
      console.error('PDF download failed, falling back to HTML:', err);
      const blob = new Blob([cleanedHtml], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fallbackFileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  const handlePdfButtonClick = (event) => {
    if (isMonthly) {
      setExcelMenuAnchor(null);
      setPdfMenuAnchor(event.currentTarget);
      return;
    }
    handleDownload(EXCEL_COLUMN_MODES.ALL);
  };

  const handleDownloadExcel = (columnMode = EXCEL_COLUMN_MODES.ALL) => {
    setExcelMenuAnchor(null);

    const doc = iframeRef.current?.contentDocument;
    if (!doc) {
      toast.error(t('NoReportData') || 'No report data');
      return;
    }

    flushPendingInlineEdit(doc, {
      onEditCommitted: handleEditCommitted,
      onRenameCommitted: handleRenameCommitted,
      source,
    });

    setDownloadingExcel(true);
    try {
      const result = downloadEditableProfitAndLossExcel({
        doc,
        reportData,
        t,
        showPercentageColumn,
        columnMode,
      });

      if (!result.ok) {
        toast.error(t('NoReportData') || 'No report data');
      }
    } catch (err) {
      console.error('Excel download failed:', err);
      toast.error(t('DownloadFailed') || 'Download failed');
    } finally {
      setDownloadingExcel(false);
    }
  };

  const handleExcelButtonClick = (event) => {
    if (isMonthly) {
      setPdfMenuAnchor(null);
      setExcelMenuAnchor(event.currentTarget);
      return;
    }
    handleDownloadExcel(EXCEL_COLUMN_MODES.ALL);
  };

  const handleApplyEdit = useCallback((silent = false) => {
    const value = editValueRef.current;
    const selected = selectedCellIdsRef.current;

    if (!selected.size) {
      if (!silent) toast.error(t('NoCellsSelected') || 'No cells selected');
      return false;
    }

    if (!String(value).trim()) {
      return false;
    }

    const parsed = parseEditableAmount(value);
    if (isNaN(parsed)) {
      if (!silent) toast.error(t('InvalidAmount') || 'Invalid amount');
      return false;
    }

    const doc = iframeRef.current?.contentDocument;
    applyValueToSelectedCells(doc, selected, parsed, { onEditCommitted: handleEditCommitted, source });
    setEditValue(formatEditableAmount(parsed).trim());
    return true;
  }, [t, handleEditCommitted, source]);

  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApplyEdit();
    }
  };

  const handleEditBlur = (e) => {
    if (skipBlurApplyRef.current) return;
    if (toolbarRef.current?.contains(e.relatedTarget)) return;
    if (!selectedCellIdsRef.current.size) return;
    if (!String(editValueRef.current).trim()) return;
    handleApplyEdit(true);
  };

  const handleToolbarMouseDown = () => {
    skipBlurApplyRef.current = true;
    window.setTimeout(() => {
      skipBlurApplyRef.current = false;
    }, 200);
  };

  const handleClearSelection = () => {
    const doc = iframeRef.current?.contentDocument;
    clearSelectionInDocument(doc);
    setSelectedCellIds(new Set());
    setEditValue('');
  };

  const handleResetChanges = () => {
    const doc = iframeRef.current?.contentDocument;
    flushPendingInlineEdit(doc, {
      onEditCommitted: handleEditCommitted,
      onRenameCommitted: handleRenameCommitted,
      source,
    });

    if (cleanupEditableRef.current) {
      cleanupEditableRef.current();
      cleanupEditableRef.current = null;
    }

    localEditsRef.current.clear();
    localRenamesRef.current.clear();
    localDeletedRowsRef.current.clear();
    localAddedRowsRef.current = [];
    addedRowCounterRef.current = 0;
    setSelectedCellIds(new Set());
    setEditValue('');

    const baselineHtml = originalHtmlRef.current || htmlContent;
    if (baselineHtml) {
      setIframeHtmlContent(baselineHtml);
      setIframeReloadToken((token) => token + 1);
    }
  };

  if (!open) return null;

  const selectedCount = selectedCellIds.size;

  return (
    <div className="editable-pl-overlay" dir={direction}>
      <div
        className={`editable-pl-modal ${isMonthly ? 'editable-pl-modal--monthly' : 'editable-pl-modal--yearly'}${isMonthly && showPercentageColumn ? ' editable-pl-modal--monthly-with-pct' : ''}`}
        dir={direction}
      >
        <div className={`editable-pl-header ${isRtl ? 'rtl' : 'ltr'}`} dir={direction}>
          <IconButton
            size="small"
            onClick={onCancel}
            sx={{
              color: '#666',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)', color: '#000' },
            }}
          >
            <CloseIcon />
          </IconButton>
          <div className="editable-pl-title">{t(title)}</div>
          <div style={{ width: 40 }} />
        </div>

        <div
          ref={toolbarRef}
          className={`editable-pl-toolbar ${isRtl ? 'rtl' : 'ltr'}`}
          dir={direction}
          onMouseDown={handleToolbarMouseDown}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={!isRtl ? <PrintIcon /> : undefined}
            endIcon={isRtl ? <PrintIcon /> : undefined}
            onClick={handlePrint}
            sx={{ borderColor: '#304FFF', color: '#304FFF' }}
          >
            {t('Print')}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={
              !isRtl ? (
                <>
                  <DownloadIcon />
                  {isMonthly ? <ArrowDropDownIcon /> : null}
                </>
              ) : undefined
            }
            endIcon={
              isRtl ? (
                <>
                  <DownloadIcon />
                  {isMonthly ? <ArrowDropDownIcon /> : null}
                </>
              ) : undefined
            }
            onClick={handlePdfButtonClick}
            disabled={downloading}
            aria-haspopup={isMonthly ? 'menu' : undefined}
            aria-expanded={isMonthly && Boolean(pdfMenuAnchor) ? 'true' : undefined}
            sx={{ borderColor: '#304FFF', color: '#304FFF' }}
          >
            {t('Download')}
          </Button>
          {isMonthly && (
            <Menu
              anchorEl={pdfMenuAnchor}
              open={Boolean(pdfMenuAnchor)}
              onClose={() => setPdfMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: isRtl ? 'right' : 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: isRtl ? 'right' : 'left' }}
              disableScrollLock
              sx={{ zIndex: 4000 }}
              ModalProps={{ sx: { zIndex: 4000 } }}
              MenuListProps={{ dir: direction, dense: true }}
            >
              <MenuItem
                dir={direction}
                disabled={downloading}
                onClick={() => handleDownload(EXCEL_COLUMN_MODES.TOTALS)}
              >
                {t('DownloadExcelTotals')}
              </MenuItem>
              <MenuItem
                dir={direction}
                disabled={downloading}
                onClick={() => handleDownload(EXCEL_COLUMN_MODES.MONTHS)}
              >
                {t('DownloadExcelByMonths')}
              </MenuItem>
            </Menu>
          )}
          {showExcelDownload && (
            <>
              <Button
                variant="outlined"
                size="small"
                startIcon={
                  !isRtl ? (
                    <>
                      <TableChartIcon />
                      {isMonthly ? <ArrowDropDownIcon /> : null}
                    </>
                  ) : undefined
                }
                endIcon={
                  isRtl ? (
                    <>
                      <TableChartIcon />
                      {isMonthly ? <ArrowDropDownIcon /> : null}
                    </>
                  ) : undefined
                }
                onClick={handleExcelButtonClick}
                disabled={downloadingExcel}
                aria-haspopup={isMonthly ? 'menu' : undefined}
                aria-expanded={isMonthly && Boolean(excelMenuAnchor) ? 'true' : undefined}
                sx={{ borderColor: '#304FFF', color: '#304FFF' }}
              >
                {t('DownloadExcelFile')}
              </Button>
              <Menu
                anchorEl={excelMenuAnchor}
                open={Boolean(excelMenuAnchor)}
                onClose={() => setExcelMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: isRtl ? 'right' : 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: isRtl ? 'right' : 'left' }}
                disableScrollLock
                sx={{ zIndex: 4000 }}
                ModalProps={{ sx: { zIndex: 4000 } }}
                MenuListProps={{ dir: direction, dense: true }}
              >
                <MenuItem
                  dir={direction}
                  disabled={downloadingExcel}
                  onClick={() => handleDownloadExcel(EXCEL_COLUMN_MODES.TOTALS)}
                >
                  {t('DownloadExcelTotals')}
                </MenuItem>
                <MenuItem
                  dir={direction}
                  disabled={downloadingExcel}
                  onClick={() => handleDownloadExcel(EXCEL_COLUMN_MODES.MONTHS)}
                >
                  {t('DownloadExcelByMonths')}
                </MenuItem>
              </Menu>
            </>
          )}

          <ToggleButtonGroup
            size="small"
            value={multiSelectMode ? 'multi' : 'single'}
            exclusive
            onChange={(_, val) => {
              if (val !== null) {
                setMultiSelectMode(val === 'multi');
                handleClearSelection();
              }
            }}
          >
            <ToggleButton value="single" sx={{ fontSize: '0.75rem' }}>
              {t('SingleSelect')}
            </ToggleButton>
            <ToggleButton value="multi" sx={{ fontSize: '0.75rem' }}>
              {t('MultiSelectMode')}
            </ToggleButton>
          </ToggleButtonGroup>

          <div className="editable-pl-toolbar-checkbox">
            <Checkbox
              checked={showPercentageColumn}
              onChange={(event) => handleShowPercentageColumnToggle(event.target.checked)}
              name="ShowPercentageColumn"
              color="primary"
              size="small"
            />
            <span className="editable-pl-toolbar-checkbox-label">
              {t('ShowPercentageColumn')}
            </span>
          </div>

          {multiSelectMode && (
            <>
              <TextField
                className="editable-pl-toolbar-field"
                inputRef={editInputRef}
                size="small"
                type="text"
                placeholder={t('Amount') || 'סכום'}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={handleEditKeyDown}
                onBlur={handleEditBlur}
                disabled={!selectedCount}
                inputProps={{
                  style: { textAlign: isRtl ? 'right' : 'left', direction },
                }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={() => handleApplyEdit()}
                disabled={!selectedCount || !String(editValue).trim()}
                sx={{ bgcolor: '#304FFF' }}
              >
                {t('ApplyEdit')}
                {selectedCount > 0 ? ` (${selectedCount})` : ''}
              </Button>
            </>
          )}

          <div className="editable-pl-action-group" dir={direction}>
            <button
              type="button"
              className="editable-pl-action-btn editable-pl-action-btn--clear"
              onClick={handleClearSelection}
              disabled={!selectedCount}
              title={t('ClearSelection')}
            >
              <ClearAllIcon fontSize="inherit" />
              <span>{t('ClearSelection')}</span>
            </button>
            <button
              type="button"
              className="editable-pl-action-btn editable-pl-action-btn--reset"
              onClick={handleResetChanges}
              title={t('ResetChanges')}
            >
              <RestartAltIcon fontSize="inherit" />
              <span>{t('ResetChanges')}</span>
            </button>
          </div>
        </div>

        <div className="editable-pl-content" dir={direction}>
          {htmlUrl ? (
            <iframe
              key={`editable-pl-iframe-${iframeReloadToken}`}
              ref={iframeRef}
              className="editable-pl-iframe"
              src={htmlUrl}
              title={t(title)}
              onLoad={handleIframeLoad}
              scrolling="auto"
            />
          ) : (
            <div className="editable-pl-loading">{t('Loading')}...</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditableProfitAndLossHtmlPreview;
